import { useEffect, useRef, useState } from "react";
import { SettingsRequestError, settingsApi } from "./api";
import { Skeleton, SkeletonGrid } from "./Skeleton";
import type { AdminCharacterCreator, CharacterManagementItem, GuildMemberPage } from "./types";

const DISCORD_UID_PATTERN = /^\d{16,20}$/;

type CreatorTransferTarget = Pick<CharacterManagementItem, "id" | "name" | "creator_uid" | "creator_name" | "creator_revision">;

async function loadCurrentCharacter(characterId: string): Promise<CreatorTransferTarget> {
  const current = (await settingsApi.loadUserCharacters()).find((character) => character.id === characterId);
  if (!current) throw new Error("character_creator_not_found");
  return current;
}

export function CreatorTransferDialog({ character, onClose, onTransferred, onStateReloaded, loadMembers = settingsApi.loadGuildMembers, reloadCharacter = loadCurrentCharacter, updateCreator = settingsApi.updateAdminCharacterCreator }: {
  character: CreatorTransferTarget;
  onClose: () => void;
  onTransferred: () => void;
  onStateReloaded?: () => void;
  loadMembers?: (query?: string, cursor?: string) => Promise<GuildMemberPage>;
  reloadCharacter?: (characterId: string) => Promise<CreatorTransferTarget>;
  updateCreator?: (characterId: string, creatorUid: string | null, expectedCreatorRevision: number, requestId: string) => Promise<AdminCharacterCreator>;
}) {
  const [serverCharacter, setServerCharacter] = useState(character);
  const [creatorUid, setCreatorUid] = useState<string | null>(character.creator_uid);
  const [query, setQuery] = useState("");
  const [members, setMembers] = useState<GuildMemberPage["members"]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [conflict, setConflict] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [reloadNotice, setReloadNotice] = useState("");
  const requestId = useRef<{ payloadKey: string; value: string } | null>(null);
  const requestGeneration = useRef(0);
  const savingRef = useRef(false);
  const reloadingRef = useRef(false);
  const dialogRef = useRef<HTMLElement>(null);
  const uidRef = useRef<HTMLInputElement>(null);

  const normalizedUid = creatorUid?.trim() ?? null;
  const uidValid = normalizedUid === null || DISCORD_UID_PATTERN.test(normalizedUid);
  const changed = normalizedUid !== serverCharacter.creator_uid;

  function requestIdFor(payloadKey: string): string {
    if (requestId.current?.payloadKey !== payloadKey) requestId.current = { payloadKey, value: crypto.randomUUID() };
    return requestId.current.value;
  }

  function searchMembers(search: string, cursor?: string, append = false): void {
    const generation = ++requestGeneration.current;
    setMembersLoading(true);
    setMembersError("");
    void loadMembers(search, cursor).then((page) => {
      if (requestGeneration.current !== generation) return;
      setMembers((current) => append ? [...current, ...page.members] : page.members);
      setNextCursor(page.next_cursor);
    }).catch(() => {
      if (requestGeneration.current !== generation) return;
      setMembersError("无法读取服务器成员，请稍后重试。你仍可直接粘贴成员 UID。");
      if (!append) setMembers([]);
    }).finally(() => {
      if (requestGeneration.current === generation) setMembersLoading(false);
    });
  }

  useEffect(() => {
    const restoreTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    uidRef.current?.focus();
    searchMembers("");
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape" && !savingRef.current && !reloadingRef.current) { event.preventDefault(); onClose(); return; }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>("button:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex='-1'])")];
      if (focusable.length === 0) { event.preventDefault(); return; }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      requestGeneration.current += 1;
      document.removeEventListener("keydown", handleKeyDown);
      restoreTarget?.focus();
    };
  }, [character.id]);

  async function submit(): Promise<void> {
    if (savingRef.current || saving || conflict || !changed || !uidValid || !confirmed) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError("");
    setReloadNotice("");
    try {
      const payloadKey = `${serverCharacter.id}|${serverCharacter.creator_revision}|${normalizedUid ?? "system"}`;
      await updateCreator(serverCharacter.id, normalizedUid, serverCharacter.creator_revision, requestIdFor(payloadKey));
      savingRef.current = false;
      setSaving(false);
      onTransferred();
      return;
    } catch (cause) {
      const isConflict = cause instanceof SettingsRequestError && cause.status === 409;
      setConflict(isConflict);
      if (isConflict) setConfirmed(false);
      setSaveError(cause instanceof SettingsRequestError && cause.status === 409
        ? "作者已在别处变更。当前草稿已保留，请重新加载最新角色状态。"
        : cause instanceof SettingsRequestError && cause.code === "character_creator_not_guild_member"
          ? "这个 UID 不是当前服务器成员。当前草稿已保留，请重新选择。"
          : "作者变更失败。当前草稿已保留，请稍后重试。");
      savingRef.current = false;
      setSaving(false);
    }
  }

  async function reloadState(): Promise<void> {
    if (reloadingRef.current || reloading || savingRef.current) return;
    reloadingRef.current = true;
    setReloading(true);
    setSaveError("");
    setReloadNotice("");
    try {
      const current = await reloadCharacter(serverCharacter.id);
      setServerCharacter(current);
      requestId.current = null;
      setConflict(false);
      setConfirmed(false);
      setReloadNotice("已加载最新作者状态；UID 草稿仍保留，请核对并重新确认。");
      onStateReloaded?.();
    } catch {
      setSaveError("无法重新加载角色状态。UID 草稿仍保留，请稍后重试。");
    } finally {
      reloadingRef.current = false;
      setReloading(false);
    }
  }

  const currentCreator = serverCharacter.creator_uid === null
    ? "系统"
    : `${serverCharacter.creator_name ?? "未命名成员"}（${serverCharacter.creator_uid}）`;

  return <div className="member-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !savingRef.current && !reloadingRef.current) onClose(); }}>
    <section ref={dialogRef} className="member-dialog creator-transfer-dialog" role="dialog" aria-modal="true" aria-labelledby="creator-transfer-title">
      <header><div><h3 id="creator-transfer-title">转让作者 · {serverCharacter.name}</h3><p>当前作者：{currentCreator} · 修订 {serverCharacter.creator_revision}</p></div><button type="button" aria-label="关闭作者转让" disabled={saving || reloading} onClick={onClose}>×</button></header>
      <p className="privacy-note">这是全局变更。新作者将获得该角色后续共享编辑权限；旧作者刷新目录后将失去这些权限。清除作者会把角色归为系统角色。</p>
      <label>作者 UID<input ref={uidRef} aria-label="作者 UID" type="text" inputMode="numeric" maxLength={20} disabled={saving || reloading || creatorUid === null} value={creatorUid ?? ""} aria-invalid={!uidValid} placeholder="粘贴 16–20 位 Discord UID" onChange={(event) => { setCreatorUid(event.target.value); setConfirmed(false); setSaveError(""); setReloadNotice(""); }} /></label>
      <label className="memory-permanent"><input type="checkbox" checked={creatorUid === null} disabled={saving || reloading} onChange={(event) => { setCreatorUid(event.target.checked ? null : serverCharacter.creator_uid ?? ""); setConfirmed(false); setSaveError(""); setReloadNotice(""); }} /><span className="switch-track" aria-hidden="true" /><span>清除作者并归为系统角色</span></label>
      {!uidValid ? <p className="action-error" role="alert">作者 UID 必须是 16–20 位数字。</p> : null}
      <form className="member-search" onSubmit={(event) => { event.preventDefault(); searchMembers(query); }}><input type="search" maxLength={80} value={query} aria-label="搜索新作者" placeholder="搜索服务器成员名称…" disabled={saving || reloading} onChange={(event) => setQuery(event.target.value)} /><button className="primary" type="submit" disabled={membersLoading || saving || reloading}>搜索</button></form>
      {membersError ? <p className="action-error" role="alert">{membersError}</p> : null}
      <div className="member-list" aria-busy={membersLoading}>{membersLoading && members.length === 0 ? <SkeletonGrid items={4} variant="row" label="正在读取服务器成员" /> : !membersLoading && members.length === 0 && !membersError ? <p className="panel-state">没有匹配的成员。</p> : members.map((member) => <button className={`member-row${normalizedUid === member.uid ? " selected" : ""}`} type="button" key={member.uid} aria-pressed={normalizedUid === member.uid} disabled={saving || reloading} onClick={() => { setCreatorUid(member.uid); setConfirmed(false); setSaveError(""); setReloadNotice(""); }}><span className="member-avatar" aria-hidden="true">{member.avatar_url ? <img src={member.avatar_url} alt="" /> : member.name.slice(0, 1)}</span><span>{member.name}<small>{member.uid}</small></span></button>)}</div>
      <div className="member-page-state">{membersLoading && members.length > 0 ? <Skeleton width="7rem" label="正在读取更多成员" /> : nextCursor ? <button className="secondary outlined" type="button" disabled={saving} onClick={() => searchMembers(query, nextCursor, true)}>加载更多</button> : members.length > 0 ? "已经到底了" : null}</div>
      <label className="creator-transfer-confirm"><input type="checkbox" checked={confirmed} disabled={saving || reloading || conflict || !changed || !uidValid} onChange={(event) => setConfirmed(event.target.checked)} />我确认这是全局作者变更，并会影响共享编辑与未来互动记录权限。</label>
      {saveError ? <p className="action-error" role="alert">{saveError}</p> : null}
      {reloadNotice ? <p className="action-success" role="status">{reloadNotice}</p> : null}
      <div className="profile-editor-actions"><button className="secondary outlined" type="button" disabled={saving || reloading} onClick={onClose}>取消</button>{conflict ? <button className="secondary outlined" type="button" disabled={reloading} onClick={() => void reloadState()}>{reloading ? "加载中…" : "重新加载角色状态"}</button> : null}<button className="shared-save-button" type="button" disabled={saving || reloading || conflict || !changed || !uidValid || !confirmed} onClick={() => void submit()}>{saving ? "保存中…" : normalizedUid === null ? "确认归为系统角色" : "确认转让作者"}</button></div>
    </section>
  </div>;
}
